import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from './Button';
import { Modal, type ModalSize } from './Modal';

/**
 * DIALOG OG SKUFF — de to flatene produktet har, og ingen tredje (QA-runde 12, K-7 / J1).
 *
 * ================================================================================================
 * HVORFOR DENNE FILA FINNES.
 *
 * Rapporten talte opp dagens bruk og fant ingen regel: «Inviter til Tvitvi, Ny festival, Ny årgang,
 * Ny scene, forespørselsmodal, transfer-popup, artistpopup … » som modal, og «Ny kontakt, Rediger
 * kontakt, Ny/Rediger ressurstype» som skuff, uten at noen hadde bestemt hvilken som er hvilken.
 * «Å opprette en kontakt og å opprette en scene føles som to forskjellige apper.»
 *
 * `Modal` kunne allerede begge deler — `placement="right"` har vært der siden fase 0 — så det som
 * manglet var ikke en evne, det var et NAVN. Så lenge begge heter det samme, tas valget på nytt
 * hver gang noen skriver `<Modal>`, og da varierer det per økt. To komponenter med hvert sitt navn
 * gjør valget til noe man tar én gang, synlig i importlinja.
 *
 * ================================================================================================
 * REGELEN (J1-tabellen, avgjort som K-7). Den står her fordi det er her man leser den:
 *
 *   | Bekreftelse, slett, faresone                                  | `Dialog size="sm"`       |
 *   | Kort oppretting, ca. 5 felt eller færre, uten kontekstbehov   | `Dialog`                 |
 *   | Stor beslutning som må tas før man går videre                 | `Dialog size="lg"`       |
 *   | Opprette/redigere objekter med mange felt, veiviser, kart     | `Sheet`                  |
 *   | Detaljvisning man vil bla mellom (neste/forrige rad)          | `Sheet`                  |
 *   | Modal over modal                                              | **Aldri** — se under     |
 *
 * ================================================================================================
 * DET DE HAR FELLES, OG SOM ER HELE POENGET:
 *
 *  - **Samme header.** Tittel, valgfri beskrivelse, ikonknapper og kryss — `Modal` tegner den, og
 *    ingen av de to har lov til å tegne sin egen.
 *  - **Samme knapperekkefølge: Avbryt, så primær, til høyre.** Ikke en `footer`-slot kallstedet
 *    fyller som det vil — det var slik #30a oppsto («Send forespørsel» sto til venstre). Footeren
 *    bygges av `cancel` og `primary` her, og rekkefølgen er ikke et argument.
 *  - **Samme Esc/Enter.** Esc lukker (Radix). Enter lagrer, fordi innholdet ER et `<form>` og
 *    primærknappen er dens `submit` — se `asForm`.
 *  - **Det som er fylt inn, forsvinner aldri uten et spørsmål** (QA16 funn 4). Med `dirty` spør Esc,
 *    klikk utenfor, krysset og Avbryt først: «Forkaste det du har fylt inn?» i bunnlinja — ikke en
 *    dialog over dialogen. Uten noe fylt inn lukker de som før.
 *  - **Aldri modal over modal.** `Modal` melder seg i `modal-nesting.ts` og roper i konsollen
 *    hvis den åpnes over en annen. Testen `modal-nesting.test.tsx` holder ropet i live.
 *
 * ================================================================================================
 * ENTER LAGRER, OG HVORDAN.
 *
 * Footeren står UTENFOR den rullende kroppen — den skal ikke rulle bort — så primærknappen kan
 * ikke stå inne i skjemaet den sender. HTMLs `form`-attributt binder dem sammen på tvers av
 * DOM-en, og da er Enter i et hvilket som helst felt den samme handlingen som et klikk på knappen.
 * Det er nettleserens egen mekanikk, ikke en tastelytter, så en `disabled` knapp betyr at Enter
 * heller ikke sender.
 *
 * FØLGEN: KALLSTEDET TEGNER IKKE SITT EGET `<form>`. Et skjema inne i et skjema er ugyldig HTML og
 * nettleseren fjerner det indre. Har flaten ikke noe å lagre — en lesevisning, et kart — settes
 * `asForm={false}`, og da er primærknappen en vanlig knapp igjen.
 */

/** Primærhandlingen. Alltid helt til høyre, alltid den samme knappen. */
export interface SurfaceAction {
  label: string;
  onAction: () => void;
  loading?: boolean;
  disabled?: boolean;
  /** `danger` for en sletting som ikke kan angres. Alt som KAN angres bruker toast (§11.4). */
  tone?: 'primary' | 'danger';
  'data-testid'?: string;
}

interface SurfaceProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  titleHidden?: boolean;
  description?: string;
  children: ReactNode;
  headerActions?: ReactNode;
  /** Se `Modal`: `false` fjerner krysset for en dialog som med vilje ikke lar seg lukke. */
  dismissible?: boolean;
  primary?: SurfaceAction;
  /**
   * Teksten på Avbryt. Utelat den for «Avbryt»; sett den der ordet er feil («Ikke nå», «Lukk»).
   * `null` fjerner knappen — for en flate der det eneste valget er å lukke via krysset.
   */
  cancelLabel?: string | null;
  /** Noe som skal stå til VENSTRE i footeren, som «Slett» i en redigeringsskuff. */
  footerStart?: ReactNode;
  /** Av for en flate uten noe å lagre. Se «Enter lagrer, og hvordan». */
  asForm?: boolean;
  /**
   * Noe er fylt inn (QA16 funn 4): et lukkeforsøk spør først om det skal forkastes. Kallstedet sier
   * hva «fylt inn» er; flaten eier spørsmålet, så det er likt overalt.
   */
  dirty?: boolean;
  /**
   * Høyden krymper ikke mens flaten er åpen (en veiviser, QA16 funn 4). Et valg som tar bort innhold,
   * flytter ellers knappene og kanten opp, og neste klikk lander på overlegget — «utenfor».
   */
  steady?: boolean;
  className?: string;
  'data-testid'?: string;
}

function Surface({
  open,
  onOpenChange,
  title,
  titleHidden,
  description,
  children,
  headerActions,
  dismissible,
  primary,
  cancelLabel,
  footerStart,
  asForm = true,
  dirty = false,
  steady = false,
  placement,
  size,
  className,
  'data-testid': testId,
}: SurfaceProps & { placement: 'center' | 'right'; size: ModalSize }): ReactNode {
  const { t } = useTranslation();
  const formId = useId();
  /* Lukkeforsøket med noe fylt inn: bunnlinja spør. Et nytt forsøk mens den spør, er «nei, bli». */
  const [asking, setAsking] = useState(false);
  useEffect(() => {
    if (!dirty || !open) setAsking(false);
  }, [dirty, open]);
  const requestOpenChange = (next: boolean): void => {
    if (next || !dirty) {
      onOpenChange(next);
      return;
    }
    setAsking((current) => !current);
  };

  const cancel = cancelLabel === null ? null : (cancelLabel ?? t('common.actions.cancel'));
  const hasFooter = primary !== undefined || cancel !== null || footerStart !== undefined;
  const floor = useNeverShrink(steady);

  /*
   * SKJEMAET TEGNES SÅ LENGE FLATEN ER ET SKJEMA — IKKE BARE NÅR DEN HAR NOE Å LAGRE.
   *
   * ================================================================================================
   * Her sto `asForm && primary !== undefined`, og det kostet hele innholdets tilstand hver gang
   * primærknappen kom eller gikk mens flaten sto åpen. React sammenligner elementTYPEN på hver
   * plass: `<form>` det ene sekundet og `children` det neste er to forskjellige trær, så alt under
   * ble avmontert og bygget på nytt.
   *
   * Målt på artistkortet (QA12, `booking.spec:93`): man åpner Pris-fanen, trykker blyanten — som
   * er det som gir kortet en primærknapp — og står plutselig på Oversikt igjen, i redigering av
   * feil ting. Fanen er en ukontrollert Radix-fane, så den falt tilbake til `defaultValue`. Det
   * samme ville skjedd med et halvskrevet felt, en åpen seksjon eller en rulleposisjon, i HVILKEN
   * SOM HELST dialog som får eller mister primærknappen mens den står åpen.
   *
   * Nå er formen stabil: er flaten et skjema, er den alltid et skjema. Uten en primærhandling har
   * innsendingen ingenting å gjøre, og den blir stoppet her framfor å la nettleseren laste siden.
   */
  const body = asForm ? (
    <form
      id={formId}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (primary === undefined || primary.disabled === true || primary.loading === true) return;
        primary.onAction();
      }}
    >
      {steady ? (
        <div ref={floor.ref} style={{ minHeight: floor.minHeight }}>
          {children}
        </div>
      ) : (
        children
      )}
    </form>
  ) : (
    children
  );

  return (
    <Modal
      open={open}
      onOpenChange={requestOpenChange}
      title={title}
      {...(titleHidden === undefined ? {} : { titleHidden })}
      {...(description === undefined ? {} : { description })}
      {...(headerActions === undefined ? {} : { headerActions })}
      {...(dismissible === undefined ? {} : { dismissible })}
      placement={placement}
      size={size}
      {...(className === undefined ? {} : { className })}
      {...(testId === undefined ? {} : { 'data-testid': testId })}
      {...(asking
        ? {
            footer: (
              <>
                <span
                  className="mr-auto text-sm font-medium text-default"
                  role="alert"
                  data-testid="surface-discard-question"
                >
                  {t('common.discard.question')}
                </span>
                <Button onClick={() => setAsking(false)} data-testid="surface-discard-keep">
                  {t('common.discard.keep')}
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    setAsking(false);
                    onOpenChange(false);
                  }}
                  data-testid="surface-discard-confirm"
                >
                  {t('common.discard.confirm')}
                </Button>
              </>
            ),
          }
        : hasFooter
          ? {
              footer: (
                <>
                  {/* Til venstre, og `mr-auto` er det som holder de to andre til høyre. */}
                  {footerStart === undefined ? null : (
                    <span className="mr-auto flex items-center gap-2">{footerStart}</span>
                  )}
                  {cancel === null ? null : (
                    <Button onClick={() => requestOpenChange(false)} data-testid="surface-cancel">
                      {cancel}
                    </Button>
                  )}
                  {primary === undefined ? null : (
                    <Button
                      // `type="submit"` + `form` binder knappen til skjemaet i kroppen, som er det
                      // som gjør Enter til den samme handlingen. Se hodet.
                      {...(asForm ? { type: 'submit' as const, form: formId } : {})}
                      variant={primary.tone === 'danger' ? 'danger' : 'primary'}
                      loading={primary.loading === true}
                      disabled={primary.disabled === true}
                      onClick={asForm ? undefined : primary.onAction}
                      data-testid={primary['data-testid'] ?? 'surface-primary'}
                    >
                      {primary.label}
                    </Button>
                  )}
                </>
              ),
            }
          : {})}
    >
      {body}
    </Modal>
  );
}

/** Den største høyden innholdet har hatt siden flaten åpnet; `steady` holder den som gulv. */
function useNeverShrink(on: boolean): {
  ref: (node: HTMLDivElement | null) => void;
  minHeight: number;
} {
  const [minHeight, setMinHeight] = useState(0);
  const observer = useRef<ResizeObserver | null>(null);
  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      observer.current?.disconnect();
      if (!on || node === null || typeof ResizeObserver === 'undefined') return;
      observer.current = new ResizeObserver(([entry]) => {
        const height = Math.ceil(entry?.contentRect.height ?? 0);
        setMinHeight((current) => (height > current ? height : current));
      });
      observer.current.observe(node);
    },
    [on],
  );
  return { ref, minHeight };
}

export type DialogSize = 'sm' | 'md' | 'lg';

export interface DialogProps extends SurfaceProps {
  /**
   * `sm` for en bekreftelse, `md` for en kort oppretting, `lg` for en stor beslutning
   * (transfer-popupen). Det finnes ikke noe større: er flaten større enn dette, er den en `Sheet`.
   */
  size?: DialogSize;
}

/** Sentrert, blokkerende. For det korte. Se regeltabellen i hodet på fila. */
export function Dialog({ size = 'md', ...rest }: DialogProps): ReactNode {
  return <Surface {...rest} placement="center" size={size} />;
}

/**
 * Bredden på en skuff.
 *
 * `wide` er 720 px, og tallet er Atles: artistpopupen i Booking er godkjent som en BRED skuff
 * (K-7 / J1). `md` (512) er standarden for et vanlig skjema, `lg` (672) for en veiviser med to
 * kolonner, `xl` (760) er `EntityPanel`-bredden som alt fantes.
 */
export type SheetWidth = 'md' | 'lg' | 'wide' | 'xl';

const SHEET_SIZE: Record<SheetWidth, ModalSize> = {
  md: 'md',
  lg: 'lg',
  wide: 'wide',
  xl: 'xl',
};

export interface SheetProps extends SurfaceProps {
  width?: SheetWidth;
}

/** Fra høyre, siden bak er synlig. For det lange. Se regeltabellen i hodet på fila. */
export function Sheet({ width = 'md', ...rest }: SheetProps): ReactNode {
  return <Surface {...rest} placement="right" size={SHEET_SIZE[width]} />;
}
